/**
 * Path prefixes that require a valid JWT in `AUTH_COOKIE_NAME`.
 * Keep in sync with `middleware.ts` → `config.matcher` (derived via `protectedPathMatchers`).
 */
export const PROTECTED_PATH_PREFIXES = [
  "/overview",
  "/fleet",
  "/dashboard",
  "/devices",
  "/live",
  "/explorer",
  "/telemetry",
  "/alerts",
  "/commands",
  "/analytics",
  "/dashboards",
  "/automations",
  "/reports",
  "/users",
  "/profile",
  "/tenants",
  "/permissions",
  "/settings",
  "/entitlements",
  "/activity-policy",
  "/organizations",
  "/billing",
  "/support",
  "/security",
  "/system-health",
] as const;

/**
 * Static middleware matcher — must be a compile-time literal array (no `flatMap`/`map` at
 * module scope) so Next.js can parse `middleware.ts` `config.matcher`.
 */
export const MIDDLEWARE_MATCHER: string[] = [
  "/overview",
  "/overview/:path*",
  "/fleet",
  "/fleet/:path*",
  "/dashboard",
  "/dashboard/:path*",
  "/devices",
  "/devices/:path*",
  "/live",
  "/live/:path*",
  "/explorer",
  "/explorer/:path*",
  "/telemetry",
  "/telemetry/:path*",
  "/alerts",
  "/alerts/:path*",
  "/commands",
  "/commands/:path*",
  "/analytics",
  "/analytics/:path*",
  "/dashboards",
  "/dashboards/:path*",
  "/automations",
  "/automations/:path*",
  "/reports",
  "/reports/:path*",
  "/users",
  "/users/:path*",
  "/profile",
  "/profile/:path*",
  "/tenants",
  "/tenants/:path*",
  "/permissions",
  "/permissions/:path*",
  "/settings",
  "/settings/:path*",
  "/entitlements",
  "/entitlements/:path*",
  "/activity-policy",
  "/activity-policy/:path*",
  "/organizations",
  "/organizations/:path*",
  "/billing",
  "/billing/:path*",
  "/support",
  "/support/:path*",
  "/security",
  "/security/:path*",
  "/system-health",
  "/system-health/:path*",
];

export const AUTH_RETURN_PATH_HEADER = "x-atech-return-path";

export const LOGIN_PATH = "/login";

export function isProtectedPathname(pathname: string): boolean {
  return PROTECTED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Only allow in-app relative paths (blocks open redirects on `?next=`).
 */
export function sanitizeInternalNextPath(raw: string | null | undefined): string {
  if (raw == null || raw === "") {
    return "/overview";
  }
  const trimmed = raw.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return "/overview";
  }
  if (trimmed.includes("://") || trimmed.includes("\\")) {
    return "/overview";
  }
  const noHash = trimmed.split("#")[0] ?? "";
  return noHash || "/overview";
}

export function loginUrlWithNext(requestUrl: string, pathnameWithSearch: string): URL {
  const url = new URL(LOGIN_PATH, requestUrl);
  url.searchParams.set("next", sanitizeInternalNextPath(pathnameWithSearch));
  return url;
}
